import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Eye,
  Plus,
  Sparkles,
  Trash2,
  Wand2,
} from "lucide-react";
import { Sheet } from "./ui/Sheet";
import { useDesign } from "../lib/store";
import { cn } from "../lib/cn";
import { formatRelative } from "../lib/formatTime";
import type { FengShuiConsultation, FengShuiRanking } from "../lib/types";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When provided and the sheet opens, auto-expand and scroll to this consultation. */
  scrollToId?: string | null;
}

// Stable empty-array reference for the consultations selector. Returning a
// fresh `[]` from the selector on every call breaks Zustand v5 + React 19's
// `useSyncExternalStore` snapshot caching ("Maximum update depth exceeded"),
// which used to fire whenever a pre-`consultations`-era saved layout was
// loaded back into the editor.
const EMPTY_CONSULTATIONS: FengShuiConsultation[] = [];
const EMPTY_RANKINGS: FengShuiRanking[] = [];

/**
 * Read-only browser for past AI feng shui work on this design: applied
 * layouts and rankings. Each layout shows the model's overall rationale
 * plus the per-piece "why" for everything the user actually applied.
 * Each ranking shows the score, summary, and the eight criteria.
 *
 * Highlight-on-canvas flashes the affected furniture via the store's
 * `flashFurnitureIds` helper; delete drops the entry from the log but
 * leaves each piece's own `rationale` tag intact (those are self-
 * contained snapshots).
 */
export function FengShuiHistorySheet({ open, onClose, scrollToId }: Props) {
  const consultations = useDesign(
    (s) => s.design.consultations ?? EMPTY_CONSULTATIONS
  );
  const rankings = useDesign((s) => s.design.rankings ?? EMPTY_RANKINGS);
  const removeConsultation = useDesign((s) => s.removeConsultation);
  const removeRanking = useDesign((s) => s.removeRanking);
  const flashFurnitureIds = useDesign((s) => s.flashFurnitureIds);

  // Sort newest first without mutating the store array. The store appends
  // chronologically, so this is normally already correct — but sorting
  // here makes the UI invariant explicit.
  const ordered = useMemo(
    () => [...consultations].sort((a, b) => b.at - a.at),
    [consultations]
  );
  const orderedRankings = useMemo(
    () => [...rankings].sort((a, b) => b.at - a.at),
    [rankings]
  );
  const empty = ordered.length === 0 && orderedRankings.length === 0;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Layout history"
      side="right"
      width="min(440px, 96vw)"
    >
      {empty ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col gap-6 pt-1">
          {ordered.length > 0 && (
            <HistoryList
              consultations={ordered}
              scrollToId={scrollToId}
              openKey={open ? "open" : "closed"}
              onRemove={removeConsultation}
              onHighlight={(ids) => flashFurnitureIds(ids)}
            />
          )}
          {orderedRankings.length > 0 && (
            <RankingList rankings={orderedRankings} onRemove={removeRanking} />
          )}
        </div>
      )}
    </Sheet>
  );
}

function EmptyState() {
  return (
    <div
      className="pt-2 rounded-2xl bg-ink-50 ring-1 ring-ink-100 px-4 py-6 text-center"
      data-testid="feng-shui-history-empty"
    >
      <div className="mx-auto size-10 rounded-full bg-white flex items-center justify-center text-ink-400 ring-1 ring-ink-200">
        <Wand2 className="size-5" />
      </div>
      <div className="mt-3 text-sm font-medium text-ink-800">
        No layouts yet
      </div>
      <div className="mt-1 text-caption text-ink-500">
        Generate a feng shui layout or rank this room — it will show up here
        so you can revisit it later.
      </div>
    </div>
  );
}

function HistoryList({
  consultations,
  scrollToId,
  openKey,
  onRemove,
  onHighlight,
}: {
  consultations: FengShuiConsultation[];
  scrollToId?: string | null;
  /** Changes whenever the sheet (re)opens so the auto-expand effect can re-fire. */
  openKey: string;
  onRemove: (id: string) => void;
  onHighlight: (ids: string[]) => void;
}) {
  // The user can manually toggle any card open or closed. We also auto-open
  // (a) the most recent consultation by default and (b) any deep-link
  // target. Both auto-opens are derived from props during render, so the
  // only state we own is the manual override map (true = forced open by
  // user click; false = forced closed by user click; absent = follow auto).
  const [manual, setManual] = useState<Map<string, boolean>>(() => new Map());
  const cardRefs = useRef(new Map<string, HTMLLIElement>());
  const defaultOpenId =
    consultations.length > 0 ? consultations[0].id : null;

  function isOpen(id: string): boolean {
    const m = manual.get(id);
    if (m !== undefined) return m;
    return id === defaultOpenId || id === scrollToId;
  }

  // The only effect: scroll the deep-link target into view once the sheet
  // re-opens. Doesn't call setState, so it can't cascade-render.
  useEffect(() => {
    if (!scrollToId) return;
    const t = window.setTimeout(() => {
      const el = cardRefs.current.get(scrollToId);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 30);
    return () => window.clearTimeout(t);
  }, [scrollToId, openKey]);

  function toggle(id: string) {
    setManual((prev) => {
      const next = new Map(prev);
      next.set(id, !isOpen(id));
      return next;
    });
  }

  return (
    <ul className="flex flex-col gap-3">
      {consultations.map((c) => {
        const open = isOpen(c.id);
        const ids = [
          ...c.applied.updates.map((u) => u.furnitureId),
          ...c.applied.additions.map((a) => a.furnitureId),
        ];
        return (
          <li
            key={c.id}
            ref={(node) => {
              if (node) cardRefs.current.set(c.id, node);
              else cardRefs.current.delete(c.id);
            }}
            className="rounded-2xl bg-white ring-1 ring-ink-200"
            data-testid="feng-shui-history-card"
          >
            <button
              type="button"
              onClick={() => toggle(c.id)}
              aria-expanded={open}
              className="w-full text-left p-3 flex items-start gap-3 rounded-2xl hover:bg-ink-50/60 transition-colors"
            >
              <ModeChip mode={c.mode} />
              <div className="min-w-0 flex-1">
                <div className="text-caption text-ink-500 flex items-center gap-1.5">
                  <span title={new Date(c.at).toLocaleString()}>
                    {formatRelative(c.at)}
                  </span>
                  <span aria-hidden>·</span>
                  <span>
                    {appliedCountLabel(c)}
                  </span>
                </div>
                {c.notes && (
                  <div className="mt-1 text-caption text-ink-600 truncate">
                    <span className="text-ink-400">Notes: </span>
                    {c.notes}
                  </div>
                )}
              </div>
              <span className="shrink-0 text-ink-400">
                {open ? (
                  <ChevronUp className="size-4" />
                ) : (
                  <ChevronDown className="size-4" />
                )}
              </span>
            </button>

            {open && (
              <div className="px-3 pb-3 -mt-1 flex flex-col gap-3">
                <div className="rounded-xl bg-ink-900 text-white p-3">
                  <div className="flex items-center gap-1.5 text-label uppercase font-semibold text-ink-300">
                    <Sparkles className="size-3" />
                    Overall
                  </div>
                  <div className="text-body-sm mt-1 leading-snug">
                    {c.overall}
                  </div>
                </div>

                {c.applied.updates.length > 0 && (
                  <AppliedSection
                    icon={<ArrowRight className="size-3.5" />}
                    label={`Moved (${c.applied.updates.length})`}
                    rows={c.applied.updates.map((u) => ({
                      title: u.furnitureName,
                      reason: u.reason,
                    }))}
                  />
                )}

                {c.applied.additions.length > 0 && (
                  <AppliedSection
                    icon={<Plus className="size-3.5" />}
                    label={`Added (${c.applied.additions.length})`}
                    rows={c.applied.additions.map((a) => ({
                      title: a.furnitureName,
                      reason: a.reason,
                    }))}
                  />
                )}

                {c.applied.removals.length > 0 && (
                  <AppliedSection
                    icon={<Trash2 className="size-3.5" />}
                    label={`Removed (${c.applied.removals.length})`}
                    variant="danger"
                    rows={c.applied.removals.map((r) => ({
                      title: r.furnitureName,
                      reason: r.reason,
                    }))}
                  />
                )}

                {(c.applied.mountUpdates?.length ?? 0) > 0 && (
                  <AppliedSection
                    icon={<ArrowRight className="size-3.5" />}
                    label={`Moved on wall (${c.applied.mountUpdates!.length})`}
                    rows={c.applied.mountUpdates!.map((u) => ({
                      title: u.mountName,
                      reason: u.reason,
                    }))}
                  />
                )}

                {(c.applied.mountAdditions?.length ?? 0) > 0 && (
                  <AppliedSection
                    icon={<Plus className="size-3.5" />}
                    label={`Hung on wall (${c.applied.mountAdditions!.length})`}
                    rows={c.applied.mountAdditions!.map((a) => ({
                      title: a.mountName,
                      reason: a.reason,
                    }))}
                  />
                )}

                {(c.applied.mountRemovals?.length ?? 0) > 0 && (
                  <AppliedSection
                    icon={<Trash2 className="size-3.5" />}
                    label={`Taken off wall (${c.applied.mountRemovals!.length})`}
                    variant="danger"
                    rows={c.applied.mountRemovals!.map((r) => ({
                      title: r.mountName,
                      reason: r.reason,
                    }))}
                  />
                )}

                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => onHighlight(ids)}
                    disabled={ids.length === 0}
                    className="h-9 px-3 rounded-lg text-xs font-medium text-ink-700 ring-1 ring-ink-200 hover:bg-ink-50 disabled:opacity-40 disabled:pointer-events-none inline-flex items-center gap-1.5"
                    title={
                      ids.length === 0
                        ? "All affected pieces have been removed"
                        : "Briefly highlight the affected floor furniture on the design canvas. Wall mounts are visible in the wall view."
                    }
                  >
                    <Eye className="size-3.5" />
                    Highlight on canvas
                  </button>
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        confirm(
                          "Delete this layout from history? Furniture stays put; only the rationale entry is removed."
                        )
                      ) {
                        onRemove(c.id);
                      }
                    }}
                    className="h-9 px-3 rounded-lg text-xs font-medium text-ink-500 hover:bg-rose-50 hover:text-rose-600 inline-flex items-center gap-1.5"
                  >
                    <Trash2 className="size-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function RankingList({
  rankings,
  onRemove,
}: {
  rankings: FengShuiRanking[];
  onRemove: (id: string) => void;
}) {
  const [manual, setManual] = useState<Map<string, boolean>>(() => new Map());
  const defaultOpenId = rankings.length > 0 ? rankings[0].id : null;

  function isOpen(id: string): boolean {
    const m = manual.get(id);
    if (m !== undefined) return m;
    return id === defaultOpenId;
  }

  function toggle(id: string) {
    setManual((prev) => {
      const next = new Map(prev);
      next.set(id, !isOpen(id));
      return next;
    });
  }

  return (
    <section>
      <h3 className="text-caption font-semibold uppercase tracking-wide text-ink-400 mb-2">
        Rankings
      </h3>
      <ul className="flex flex-col gap-3">
        {rankings.map((ranking) => {
          const open = isOpen(ranking.id);
          const score = Math.round(ranking.overall.score);
          return (
            <li
              key={ranking.id}
              className="rounded-2xl bg-white ring-1 ring-ink-200"
              data-testid="feng-shui-history-ranking"
            >
              <button
                type="button"
                onClick={() => toggle(ranking.id)}
                aria-expanded={open}
                className="w-full text-left p-3 flex items-start gap-3 rounded-2xl hover:bg-ink-50/60 transition-colors"
              >
                <span className="shrink-0 mt-0.5 inline-flex items-center justify-center min-w-10 h-6 px-2 rounded-full bg-ink-900 text-white text-label font-semibold tabular-nums">
                  {score}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-caption text-ink-500">
                    <span title={new Date(ranking.at).toLocaleString()}>
                      {formatRelative(ranking.at)}
                    </span>
                  </div>
                  {ranking.overall.summary && (
                    <p className="mt-1 text-body-sm text-ink-800 leading-snug">
                      {ranking.overall.summary}
                    </p>
                  )}
                </div>
                <span className="shrink-0 text-ink-400">
                  {open ? (
                    <ChevronUp className="size-4" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </span>
              </button>

              {open && (
                <div className="px-3 pb-3 -mt-1 flex flex-col gap-3">
                  <ul className="flex flex-col gap-1.5">
                    {ranking.criteria.map((c) => (
                      <li
                        key={c.id}
                        className="p-2.5 rounded-xl bg-ink-50 ring-1 ring-ink-100"
                      >
                        <div className="flex items-baseline gap-2">
                          <div className="text-body-sm font-semibold text-ink-900 truncate">
                            {c.label}
                          </div>
                          <div className="ml-auto text-caption font-semibold text-ink-700 tabular-nums shrink-0">
                            {Math.round(c.score)}
                          </div>
                        </div>
                        {c.rationale && (
                          <div className="text-caption text-ink-500 mt-0.5 leading-snug">
                            {c.rationale}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-2 pt-1">
                    <div className="flex-1" />
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          confirm(
                            "Delete this ranking from history? The room stays as it is; only this score is removed."
                          )
                        ) {
                          onRemove(ranking.id);
                        }
                      }}
                      className="h-9 px-3 rounded-lg text-xs font-medium text-ink-500 hover:bg-rose-50 hover:text-rose-600 inline-flex items-center gap-1.5"
                    >
                      <Trash2 className="size-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ModeChip({ mode }: { mode: FengShuiConsultation["mode"] }) {
  const label =
    mode === "rearrange"
      ? "Rearrange"
      : mode === "rearrange_suggest"
        ? "Rearrange + suggest"
        : "Full redesign";
  return (
    <span className="shrink-0 mt-0.5 inline-flex items-center gap-1 h-6 px-2 rounded-full bg-ink-900 text-white text-label font-semibold uppercase tracking-wide">
      <Sparkles className="size-3" />
      {label}
    </span>
  );
}

function AppliedSection({
  icon,
  label,
  rows,
  variant = "default",
}: {
  icon: React.ReactNode;
  label: string;
  rows: { title: string; reason: string }[];
  variant?: "default" | "danger";
}) {
  return (
    <section>
      <div className="text-caption font-semibold text-ink-500 mb-1.5 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <ul className="flex flex-col gap-1.5">
        {rows.map((r, i) => (
          <li
            key={`${r.title}-${i}`}
            className={cn(
              "p-2.5 rounded-xl ring-1",
              variant === "danger"
                ? "bg-rose-50/60 ring-rose-200"
                : "bg-ink-50 ring-ink-100"
            )}
          >
            <div className="text-body-sm font-semibold text-ink-900 truncate">
              {r.title}
            </div>
            <div className="text-caption text-ink-500 mt-0.5 leading-snug">
              {r.reason}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function appliedCountLabel(c: FengShuiConsultation): string {
  const parts: string[] = [];
  // Combine furniture + mount counts under shared verbs so the header reads
  // naturally regardless of which surface(s) the suggestion touched.
  const moved = c.applied.updates.length + (c.applied.mountUpdates?.length ?? 0);
  const added =
    c.applied.additions.length + (c.applied.mountAdditions?.length ?? 0);
  const removed =
    c.applied.removals.length + (c.applied.mountRemovals?.length ?? 0);
  if (moved > 0) parts.push(`${moved} moved`);
  if (added > 0) parts.push(`${added} added`);
  if (removed > 0) parts.push(`${removed} removed`);
  if (parts.length === 0) return "no changes applied";
  return parts.join(" · ");
}
